import React from 'react';
import PropTypes from 'prop-types';
import { Spinner, Form, Row, Col, InputGroup, Button, Dropdown, Alert } from 'react-bootstrap';
import * as FaIcons from 'react-icons/fa';

const GeneralesTable = ({
  data,
  loading,
  total,
  pageSize,
  pageNumber,
  totalPages,
  onPageChange,
  onPageSizeChange,
  onRowClick,
  searchValue,
  onSearch,
  columnas,
  title,
  onCreateClick,
  permisos,
  // Nuevas props para sorting y filtros
  sortConfig,
  onSort,
  filters,
  onFilterChange,
  getUniqueValues
}) => {

  // Componente para header con sorting y filtro
  const SortableFilterableHeader = ({ column, title }) => {
    // Determinar las claves para sorting y filtros
    const sortKey = column.sortKey || (typeof column.accessor === 'string' ? column.accessor : null);
    const filterKey = column.filterKey || (typeof column.accessor === 'string' ? column.accessor : null);
    
    // Solo mostrar filtros si tenemos las funciones necesarias y no es columna ID
    const isIdColumn = sortKey && (sortKey.toLowerCase().includes('id') || sortKey === 'id');
    const showFilters = getUniqueValues && onFilterChange && filters && !isIdColumn && filterKey;
    const showSorting = onSort && sortKey;
    
    const uniqueValues = showFilters ? getUniqueValues(filterKey) : [];
    const isActive = sortConfig?.key === sortKey;
    const direction = isActive ? sortConfig.direction : null;

    return (
      <th style={{ position: 'relative', minWidth: '150px', whiteSpace: 'nowrap' }}>
        <div className="d-flex align-items-center justify-content-between">
          <span 
            onClick={() => showSorting && onSort(sortKey)}
            style={{ cursor: showSorting ? 'pointer' : 'default', flex: 1 }}
            className="d-flex align-items-center"
          >
            {title}
            {showSorting && (
              <>
                {isActive && (
                  <FaIcons.FaSort 
                    className={`ms-1 ${direction === 'asc' ? 'text-primary' : 'text-secondary'}`}
                    style={{ 
                      transform: direction === 'desc' ? 'rotate(180deg)' : 'none'
                    }}
                  />
                )}
                {!isActive && <FaIcons.FaSort className="ms-1 text-muted" />}
              </>
            )}
          </span>
          
          {showFilters && uniqueValues.length > 0 && (
            <Dropdown>
              <Dropdown.Toggle 
                variant="link" 
                size="sm" 
                className="p-1 border-0"
                style={{ 
                  color: filters[filterKey]?.length > 0 ? '#0d6efd' : '#6c757d',
                  fontSize: '12px',
                  lineHeight: '1'
                }}
              >
                <FaIcons.FaFilter />
              </Dropdown.Toggle>

              <Dropdown.Menu 
                style={{ 
                  minWidth: '250px', 
                  maxHeight: '300px', 
                  overflowY: 'auto',
                  zIndex: 1050
                }}
              >
                <div className="p-2">
                  <div className="mb-2">
                    <Button 
                      variant="outline-secondary" 
                      size="sm" 
                      onClick={() => onFilterChange(filterKey, [])}
                      className="me-2"
                    >
                      Limpiar
                    </Button>
                    <Button 
                      variant="outline-primary" 
                      size="sm"
                      onClick={() => onFilterChange(filterKey, uniqueValues)}
                    >
                      Todos
                    </Button>
                  </div>
                  {uniqueValues.map(value => (
                    <div key={value} className="mb-1">
                      <Form.Check
                        type="checkbox"
                        id={`${filterKey}-${value}`}
                        label={value}
                        checked={filters[filterKey]?.includes(value) || false}
                        onChange={(e) => {
                          const currentFilters = filters[filterKey] || [];
                          const newFilters = e.target.checked
                            ? [...currentFilters, value]
                            : currentFilters.filter(f => f !== value);
                          onFilterChange(filterKey, newFilters);
                        }}
                      />
                    </div>
                  ))}
                </div>
              </Dropdown.Menu>
            </Dropdown>
          )}
        </div>
      </th>
    );
  };

  // Renderizar cabecera
  const renderHeader = () => (
    <tr>
      {(columnas || []).map((col, idx) => (
        <SortableFilterableHeader 
          key={idx}
          column={col} 
          title={col.Header} 
        />
      ))}
    </tr>
  );

  // Renderizar filas
  const renderRows = () => (
    data.map((row, idx) => (
      <tr 
        key={row.id || idx} 
        style={{ cursor: 'pointer' }} 
        onClick={() => onRowClick && onRowClick(row)}
        className="table-row-hover"
      >
        {(columnas || []).map((col, colIdx) => (
          <td key={colIdx}>
            {typeof col.accessor === 'function' ? col.accessor(row) : row[col.accessor]}
          </td>
        ))}
      </tr>
    ))
  );

  // Renderizar mensaje cuando no hay datos
  const renderEmptyState = () => (
    <tr>
      <td colSpan={(columnas || []).length} className="text-center py-4">
        <div className="d-flex flex-column align-items-center">
          <FaIcons.FaTable size={48} className="text-muted mb-2" />
          <p className="text-muted mb-0">No se encontraron registros</p>
          {searchValue && (
            <small className="text-muted">
              Intenta ajustar los filtros de búsqueda
            </small>
          )}
        </div>
      </td>
    </tr>
  );

  // Renderizar información de filtros aplicados
  const renderAppliedFilters = () => {
    if (!filters || Object.keys(filters).length === 0) return null;
    
    const activeFilters = Object.entries(filters).filter(([_, values]) => values && values.length > 0);
    if (activeFilters.length === 0) return null;

    return (
      <Row className="mb-2">
        <Col md={12}>
          <Alert variant="light" className="py-2 mb-0">
            <small>
              <strong>Filtros aplicados:</strong> {' '}
              {activeFilters.map(([column, values]) => {
                // Buscar el título de la columna
                const columna = (columnas || []).find(col => col.accessor === column);
                const columnTitle = columna ? columna.Header : column;
                
                return (
                  <span key={column} className="me-3">
                    <strong>{columnTitle}:</strong> {values.join(', ')}
                  </span>
                );
              })}
              <Button 
                variant="link" 
                size="sm" 
                className="p-0 ms-2"
                onClick={() => {
                  // Limpiar todos los filtros
                  Object.keys(filters).forEach(column => {
                    onFilterChange && onFilterChange(column, []);
                  });
                }}
              >
                <small>Limpiar todos</small>
              </Button>
            </small>
          </Alert>
        </Col>
      </Row>
    );
  };
  const renderSearchControls = () => (
    <Row className="mb-3 align-items-center">
      <Col md={8}>
        <InputGroup>
          <InputGroup.Text>
            <FaIcons.FaSearch />
          </InputGroup.Text>
          <Form.Control
            type="text"
            placeholder={`Buscar en ${title.toLowerCase()}...`}
            value={searchValue}
            onChange={(e) => onSearch(e.target.value)}
          />
          {searchValue && (
            <Button
              variant="outline-secondary"
              onClick={() => onSearch('')}
              title="Limpiar búsqueda"
            >
              <FaIcons.FaTimes />
            </Button>
          )}
        </InputGroup>
      </Col>
      <Col md={4} className="text-end">
        <div className="d-flex justify-content-end align-items-center gap-2">
          <span className="text-muted">
            Total: <strong>{total}</strong> registros
          </span>
          {permisos?.canCreate && (
            <Button
              variant="success"
              size="sm"
              onClick={onCreateClick}
              title={`Crear nuevo ${title.toLowerCase()}`}
            >
              <FaIcons.FaPlus className="me-1" />
              Crear
            </Button>
          )}
        </div>
      </Col>
    </Row>
  );

  // Renderizar paginación
  const renderPagination = () => (
    <div className="d-flex justify-content-between align-items-center mt-3">
      <div className="d-flex align-items-center gap-2">
        <Button 
          variant="outline-primary" 
          size="sm"
          onClick={() => onPageChange(1)} 
          disabled={pageNumber === 1}
          title="Primera página"
        >
          <FaIcons.FaAngleDoubleLeft />
        </Button>
        <Button 
          variant="outline-primary" 
          size="sm"
          onClick={() => onPageChange(pageNumber - 1)} 
          disabled={pageNumber === 1}
          title="Página anterior"
        >
          <FaIcons.FaAngleLeft />
        </Button>
        <span className="mx-3">
          Página <strong>{pageNumber}</strong> de <strong>{totalPages}</strong>
        </span>
        <Button 
          variant="outline-primary" 
          size="sm"
          onClick={() => onPageChange(pageNumber + 1)} 
          disabled={pageNumber >= totalPages}
          title="Página siguiente"
        >
          <FaIcons.FaAngleRight />
        </Button>
        <Button 
          variant="outline-primary" 
          size="sm"
          onClick={() => onPageChange(totalPages)} 
          disabled={pageNumber >= totalPages}
          title="Última página"
        >
          <FaIcons.FaAngleDoubleRight />
        </Button>
      </div>
      
      <div className="d-flex align-items-center gap-2">
        <span className="text-muted">Mostrar:</span>
        <Form.Select
          size="sm"
          value={pageSize}
          onChange={e => onPageSizeChange(Number(e.target.value))}
          style={{ width: 'auto' }}
        >
          {[10, 20, 30, 40, 50].map(size => (
            <option key={size} value={size}>{size}</option>
          ))}
        </Form.Select>
        <span className="text-muted">por página</span>
      </div>
    </div>
  );

  return (
    <div style={{ width: '100%' }}>
      {renderSearchControls()}
      {renderAppliedFilters()}
      
      {loading ? (
        <div className="d-flex justify-content-center align-items-center" style={{ height: '300px' }}>
          <div className="text-center">
            <Spinner animation="border" variant="primary" />
            <p className="mt-2 text-muted">Cargando {title.toLowerCase()}...</p>
          </div>
        </div>
      ) : (
        <>
          <div style={{ overflowX: 'auto', minHeight: '400px' }}>
            <table className="table table-hover table-striped" style={{ minWidth: '600px' }}>
              <thead className="table">
                {renderHeader()}
              </thead>
              <tbody>
                {data.length > 0 ? renderRows() : renderEmptyState()}
              </tbody>
            </table>
          </div>
          
          {total > 0 && renderPagination()}
        </>
      )}
      
      <style jsx>{`
        .table-row-hover:hover {
          background-color: #f8f9fa !important;
          transform: scale(1.01);
          transition: all 0.2s ease;
        }
      `}</style>
    </div>
  );
};

GeneralesTable.propTypes = {
  data: PropTypes.array.isRequired,
  loading: PropTypes.bool,
  total: PropTypes.number.isRequired,
  pageSize: PropTypes.number.isRequired,
  pageNumber: PropTypes.number.isRequired,
  totalPages: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  onPageSizeChange: PropTypes.func.isRequired,
  onRowClick: PropTypes.func,
  searchValue: PropTypes.string,
  onSearch: PropTypes.func.isRequired,
  columnas: PropTypes.array.isRequired,
  title: PropTypes.string.isRequired,
  onCreateClick: PropTypes.func,
  permisos: PropTypes.object,
  // Nuevas props para sorting y filtros
  sortConfig: PropTypes.object,
  onSort: PropTypes.func,
  filters: PropTypes.object,
  onFilterChange: PropTypes.func,
  getUniqueValues: PropTypes.func
};

GeneralesTable.defaultProps = {
  loading: false,
  searchValue: '',
  onRowClick: null,
  onCreateClick: null,
  permisos: {},
  sortConfig: null,
  onSort: null,
  filters: {},
  onFilterChange: null,
  getUniqueValues: null
};

export default GeneralesTable;
